import { motion } from 'framer-motion';
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Layers,
  Shield,
  Target,
  Lightbulb,
  Zap,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

const themeClasses: Record<
  string,
  { badge: string; text: string; icon: string; soft: string; tint: string; tip: string }
> = {
  blue: {
    badge: 'bg-blue-600',
    text: 'text-blue-700',
    icon: 'text-blue-600',
    soft: 'bg-blue-100',
    tint: 'bg-blue-50',
    tip: 'bg-blue-50 border-blue-500',
  },
  purple: {
    badge: 'bg-purple-600',
    text: 'text-purple-700',
    icon: 'text-purple-600',
    soft: 'bg-purple-100',
    tint: 'bg-purple-50',
    tip: 'bg-purple-50 border-purple-500',
  },
  green: {
    badge: 'bg-green-600',
    text: 'text-green-700',
    icon: 'text-green-600',
    soft: 'bg-green-100',
    tint: 'bg-green-50',
    tip: 'bg-green-50 border-green-500',
  },
  red: {
    badge: 'bg-red-600',
    text: 'text-red-700',
    icon: 'text-red-600',
    soft: 'bg-red-100',
    tint: 'bg-red-50',
    tip: 'bg-red-50 border-red-500',
  },
  orange: {
    badge: 'bg-orange-600',
    text: 'text-orange-700',
    icon: 'text-orange-600',
    soft: 'bg-orange-100',
    tint: 'bg-orange-50',
    tip: 'bg-orange-50 border-orange-500',
  },
};

export default function SwotAnalysisPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const swotQuadrants = [
    {
      title: 'Strengths',
      subtitle: 'Internal Positive Factors',
      description:
        'What you do well—internal attributes and resources that give you advantages over competitors',
      color: 'green',
      icon: CheckCircle,
      questions: [
        'What do we do better than anyone else?',
        'What unique resources do we possess?',
        'What do customers see as our strengths?',
        'What awards, certifications, or recognition have we earned?',
        'What processes or systems give us efficiency?',
      ],
      examples: [
        {
          item: 'Strong brand reputation',
          why: 'Lowers customer acquisition cost, enables premium pricing',
        },
        {
          item: 'Skilled, experienced team',
          why: 'Faster execution, higher quality, innovation capability',
        },
        {
          item: 'Proprietary technology',
          why: 'Sustainable competitive advantage, difficult to replicate',
        },
        { item: 'Financial resources', why: 'Ability to invest in growth, weather downturns' },
      ],
    },
    {
      title: 'Weaknesses',
      subtitle: 'Internal Negative Factors',
      description: 'What you need to improve—internal limitations that put you at disadvantage',
      color: 'red',
      icon: AlertTriangle,
      questions: [
        'What do we do poorly compared to competitors?',
        'What resources are we lacking?',
        'What do customers complain about?',
        'Where do we consistently underperform?',
        'What internal problems drain resources?',
      ],
      examples: [
        { item: 'Limited capital', why: 'Restricts growth investments, marketing, R&D' },
        {
          item: 'Outdated technology systems',
          why: 'Slows operations, higher costs, poor customer experience',
        },
        {
          item: 'High cost structure',
          why: "Can't compete on price, vulnerable to price pressure",
        },
        {
          item: 'Weak distribution network',
          why: 'Limited market reach, higher distribution costs',
        },
      ],
    },
    {
      title: 'Opportunities',
      subtitle: 'External Positive Factors',
      description: 'What you could exploit—external conditions you can capitalize on',
      color: 'blue',
      icon: TrendingUp,
      questions: [
        'What market trends favor us?',
        'Are there underserved customer segments?',
        'What new technologies could we leverage?',
        'Are there partnership opportunities?',
        'What regulatory changes benefit us?',
      ],
      examples: [
        {
          item: 'Growing market demand',
          why: 'Rising tide lifts all boats—easier to acquire customers',
        },
        {
          item: 'Emerging technology (AI, blockchain)',
          why: 'Early adopters gain competitive advantage',
        },
        {
          item: 'Competitor exit or weakness',
          why: 'Capture orphaned customers, gain market share',
        },
        { item: 'Changing regulations', why: 'New compliance needs create service opportunities' },
      ],
    },
    {
      title: 'Threats',
      subtitle: 'External Negative Factors',
      description: 'What could harm you—external conditions that pose risks',
      color: 'purple',
      icon: AlertTriangle,
      questions: [
        'What are competitors doing better?',
        'What disruptive technologies loom?',
        'Are regulations tightening?',
        'What economic conditions worry us?',
        'What could make our product obsolete?',
      ],
      examples: [
        {
          item: 'New well-funded competitor',
          why: 'Price pressure, talent poaching, market share loss',
        },
        { item: 'Disruptive technology', why: 'Could make current offerings obsolete overnight' },
        {
          item: 'Economic downturn',
          why: 'Reduced customer spending, higher churn, funding challenges',
        },
        { item: 'Regulatory changes', why: 'Increased compliance costs, restricted operations' },
      ],
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Internal Assessment', path: '/internal-assessment' },
              { label: 'SWOT Analysis' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Layers className="w-4 h-4 mr-2" />
            Internal Assessment Tool 1 of 3
          </span>
        }
        title="A SWOT analysis that leads to decisions"
        subtitle="Bring your strengths, weaknesses, opportunities, and threats into one clear view. Then use the connections between them to choose what your business should do next."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Build your SWOT
        </Link>
      </PageHero>

      {/* Why SWOT Matters */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Turn an honest assessment into useful choices
              </h2>
              <p className="text-xl text-gray-600">
                A SWOT list is a starting point. Its value comes from connecting internal realities
                with external conditions and deciding which combinations deserve action.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Target,
                  title: 'Clarifies Strategic Position',
                  description:
                    "Honest assessment of where you stand today—your advantages, limitations, and environmental context. You can't craft effective strategy without this foundation.",
                  impact: 'Strategy grounded in reality, not wishful thinking',
                },
                {
                  icon: Lightbulb,
                  title: 'Identifies Strategic Options',
                  description:
                    'SWOT reveals strategic paths: leverage strengths to capture opportunities, shore up weaknesses before threats exploit them, or pivot away from unfavorable conditions.',
                  impact: 'Clear action priorities emerge from analysis',
                },
                {
                  icon: Shield,
                  title: 'Anticipates Problems',
                  description:
                    'By identifying weaknesses and threats proactively, you can address vulnerabilities before they become crises. Prevention is cheaper than cure.',
                  impact: 'Risk mitigation and contingency planning',
                },
                {
                  icon: TrendingUp,
                  title: 'Focuses Resources',
                  description:
                    'Limited resources must be allocated wisely. SWOT helps prioritize: double down on strengths, fix critical weaknesses, ignore minor issues.',
                  impact: 'Resource efficiency and strategic focus',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-green-700">Impact:</span> {item.impact}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Four Quadrants */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">The Four SWOT Quadrants</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep dive into each element with questions and examples
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {swotQuadrants.map((quad, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`public-card public-card--white p-8 rounded-xl`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <quad.icon className={`w-10 h-10 ${themeClasses[quad.color].icon}`} />
                  <div>
                    <h3 className="text-2xl font-bold">{quad.title}</h3>
                    <p className={`text-sm ${themeClasses[quad.color].text} font-medium`}>
                      {quad.subtitle}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{quad.description}</p>
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Questions to Ask:</p>
                  <ul className="space-y-1">
                    {quad.questions.map((q, j) => (
                      <li key={j} className="text-xs text-gray-600 flex items-start">
                        <span className={`${themeClasses[quad.color].icon} mr-2`}>•</span>
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Examples:</p>
                  <div className="space-y-2">
                    {quad.examples.map((ex, j) => (
                      <div key={j} className="bg-white p-3 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{ex.item}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          <span className="font-semibold">Why:</span> {ex.why}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Conduct SWOT */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Conduct an Effective SWOT Analysis</h2>
              <p className="text-xl text-gray-600">
                A step-by-step process for actionable insights
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Gather Cross-Functional Team',
                  description:
                    'SWOT requires diverse perspectives. Include representatives from sales, product, operations, finance, and leadership. Each function sees different strengths and weaknesses.',
                  tip: 'Ideal team size: 5-10 people. Too small lacks perspective; too large gets unwieldy. Include at least one "outsider" (board member, advisor) for objectivity.',
                },
                {
                  step: '2',
                  title: 'Do Your Homework First',
                  description:
                    'Before the workshop, gather data: customer feedback, competitive intelligence, financial metrics, market research. SWOT is informed by evidence, not just opinions.',
                  tip: 'Assign pre-work: have participants interview 3-5 customers or review competitor websites. Come prepared with facts, not just feelings.',
                },
                {
                  step: '3',
                  title: 'Brainstorm Each Quadrant',
                  description:
                    'Spend 15-20 minutes per quadrant. Encourage quantity first (get everything on the board), then quality (evaluate and prioritize). Use sticky notes so items can be moved.',
                  tip: 'Start with Strengths (builds confidence) or Opportunities (forward-looking). Save Weaknesses for when trust is built—people are defensive about weaknesses.',
                },
                {
                  step: '4',
                  title: 'Be Brutally Honest',
                  description:
                    "The biggest SWOT failure is sugarcoating weaknesses or ignoring threats. If weaknesses surprise no one, you're being honest. If everyone is shocked, you're holding back.",
                  tip: 'Ask: "What would a competitor say our weaknesses are? What would a departing customer say?" External perspective surfaces truths we avoid.',
                },
                {
                  step: '5',
                  title: 'Prioritize and Focus',
                  description:
                    "You'll generate 20-40 items across quadrants. But not all are equal. Vote on top 5 in each quadrant. Focus beats completeness.",
                  tip: 'Use dot voting: everyone gets 5 dots to allocate however they want. Clusters reveal consensus priorities.',
                },
                {
                  step: '6',
                  title: 'Develop Strategic Implications',
                  description:
                    'Don\'t stop at the list. So what? For each priority item, ask: "What does this mean for our strategy? What actions does it suggest?"',
                  tip: 'Create strategy matrix: match Strengths to Opportunities (leverage), Strengths to Threats (defend), fix Weaknesses that enable Opportunities (grow capacity).',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Combinations */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">SWOT Strategic Combinations</h2>
              <p className="text-xl text-gray-600">
                Turn analysis into action with these four strategic approaches
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'S-O: Strengths + Opportunities',
                  subtitle: 'Aggressive Growth Strategy',
                  description:
                    'Use your strengths to capitalize on opportunities. This is your offensive playbook for winning.',
                  example:
                    'Strong AI expertise + Growing demand for automation = Launch AI consulting services',
                  color: 'green',
                },
                {
                  title: 'W-O: Weaknesses + Opportunities',
                  subtitle: 'Investment Strategy',
                  description:
                    'Fix weaknesses that prevent you from capturing opportunities. Build capabilities needed to compete.',
                  example:
                    'Weak distribution + E-commerce boom = Invest in direct-to-consumer channels',
                  color: 'blue',
                },
                {
                  title: 'S-T: Strengths + Threats',
                  subtitle: 'Defensive Strategy',
                  description:
                    'Use strengths to defend against threats. Leverage advantages to weather challenges.',
                  example:
                    'Strong brand loyalty + New low-cost competitor = Double down on premium positioning',
                  color: 'purple',
                },
                {
                  title: 'W-T: Weaknesses + Threats',
                  subtitle: 'Damage Control Strategy',
                  description:
                    'This is crisis mode. Shore up weaknesses before threats exploit them. Consider pivoting away from this area.',
                  example:
                    'High costs + Price competition = Urgently reduce cost structure or exit market',
                  color: 'red',
                },
              ].map((combo, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`public-card public-card--white p-6 rounded-xl`}
                >
                  <h3 className="text-xl font-bold mb-1">{combo.title}</h3>
                  <p className={`text-sm ${themeClasses[combo.color].text} font-semibold mb-3`}>
                    {combo.subtitle}
                  </p>
                  <p className="text-gray-700 mb-3 text-sm">{combo.description}</p>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold">Example:</span> {combo.example}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common SWOT Analysis Mistakes</h2>
              <p className="text-xl text-gray-600">Avoid these traps that make SWOT useless</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Too Vague or Generic',
                  description:
                    '"Good people" as a strength tells you nothing. "Top 10% employee retention in industry" is specific and meaningful.',
                  fix: "Be specific and quantifiable. Vague items can't guide decisions or be measured.",
                },
                {
                  mistake: 'Confusing Internal vs External',
                  description:
                    'Putting "market growth" in Strengths (it\'s an Opportunity) or "poor leadership" in Threats (it\'s a Weakness). This reveals conceptual confusion.',
                  fix: "Remember: Internal factors (you control) = S/W. External factors (you don't control) = O/T.",
                },
                {
                  mistake: 'Listing Without Prioritizing',
                  description:
                    'Creating a 40-item SWOT but treating everything equally. Not all strengths/weaknesses matter equally. Focus beats completeness.',
                  fix: 'Limit to 5-7 items per quadrant. Force prioritization. Strategic clarity requires saying "no" to less important items.',
                },
                {
                  mistake: 'Analysis Without Action',
                  description:
                    'Conducting SWOT, putting it in a deck, and never acting on it. SWOT should drive strategy decisions and resource allocation.',
                  fix: 'For each priority item, define: So what? What action does this suggest? Who owns it? When will we address it?',
                },
                {
                  mistake: 'One-Time Exercise',
                  description:
                    'Doing SWOT once and filing it away. Your competitive position changes—SWOT should be refreshed at least annually.',
                  fix: 'Review quarterly. Track: Are strengths still strengths? Have weaknesses been addressed? Are new threats emerging?',
                },
                {
                  mistake: 'Ignoring Uncomfortable Truths',
                  description:
                    "Sugarcoating weaknesses or downplaying threats because they're difficult to hear. This dooms you to repeat mistakes.",
                  fix: 'Create psychological safety. Assume positive intent. Honest assessment is a gift, not criticism. Hire external facilitator if needed.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">Move from four lists to strategic options</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Build the evidence</h3>
                <p className="text-gray-600">
                  Capture specific strengths and weaknesses alongside relevant market opportunities
                  and threats.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Make the connections</h3>
                <p className="text-gray-600">
                  Explore where strengths create opportunities and where weaknesses increase
                  exposure.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Choose the priorities</h3>
                <p className="text-gray-600">
                  Turn the most important combinations into decisions for your strategy and
                  initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/pestel-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">PESTEL Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Complement SWOT with macro-environmental analysis
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/core-competencies"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Zap className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Core Competencies</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Deep dive into strengths that create competitive advantage
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Shield className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Competitive Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Turn SWOT insights into competitive positioning
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/internal-assessment"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Back to Internal Assessment
              </Link>
              <Link
                to="/internal-assessment/core-competencies"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Core Competencies <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Build your swot in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Turn the most important combinations into decisions for your strategy and initiatives.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Build your SWOT <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
